import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import Router, { useRouter } from 'next/router';
import { useLocale, useTranslations } from 'next-intl';
import { ArrowRight } from '@components/icons/arrow/arrow';
import Headline from '@components/atoms/headline/headline';
import Copy from '../../atoms/copy/copy';
import TokenInput from '../../atoms/token-input/tokenInput';
import LoginIcon from '../../icons/login.svg';
import { fetchCsrfToken } from '../../../helper/api';
import Button from '../../atoms/button/button';
import StyledCompactLoginMask from './compactLoginMask.style';

const StickyLoginMask = ({
  action,
  headline,
  logoutHeadline,
  topLine,
  subline,
  copy,
  hintText,
  input,
  currentTokenId,
  isLoggedIn = false,
  updateLogin = () => {},
  insideNavigation = false,
  isTeaserCard = false,
  ...props
}) => {
  const locale = useLocale();
  const t = useTranslations();
  const [tokenId, setTokenId] = useState('');
  const [error, setError] = useState(false);
  const [csrfToken, setCsrfToken] = useState('');

  const router = useRouter();
  const { pathname } = router || {};

  const handleRouteChange = id => {
    updateLogin(true, id);

    const languageSpecific = locale === 'de' ? '/de/meine-ausstellung/' : '/en/my-exhibition/';

    Router.push(
      { pathname: '/token', query: { lang: locale, tokenId: id.toLowerCase(), page: 1 } },
      `${languageSpecific}${id.toLowerCase()}`
    ).then(() => window.scrollTo(0, 0));
  };

  const handleLogout = () => {
    updateLogin(false, '');

    if (pathname === '/token') {
      Router.push(
        { pathname: '/time', query: { lang: locale, slug: 'index', filters: null, page: 1 } },
        `/${locale}/`
      ).then(() => window.scrollTo(0, 0));
    }
  };

  const handleSubmit = e => {
    e.preventDefault();

    // Check if tokenID is empty
    if (tokenId === '') {
      setError(true);
      return;
    }

    // Check if is development
    if (process.env.NODE_ENV === 'development') {
      handleRouteChange(tokenId);
      return;
    }

    // Check if token exists, then login, else show error
    fetch('/de/api/token/login', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        token: tokenId
      })
    })
      .then(res => res.json())
      .then(({ message, token }) => {
        if (message === 'Success!') {
          handleRouteChange(tokenId);
        } else {
          setError(true);
          setCsrfToken(token);
        }
      })
      .catch(() => setError(true));
  };

  // Fetch CSRF Token for token id check
  useEffect(
    () =>
      fetchCsrfToken(
        token => setCsrfToken(token),
        () => setError(true)
      ),
    []
  );

  return (
    <StyledCompactLoginMask {...props}>
      <div className="loginMask__form-wrapper">
        {isLoggedIn ? (
          <>
            <div className="loginMask__current-token">
              <Copy small>
                <span className="loginMask__token-label">
                  {t('loginMask.myExhibitionCode')}
                  {': '}
                </span>
                <span className="loginMask__token">{currentTokenId}</span>
              </Copy>
            </div>
            <div className="loginMask__buttons">
              {pathname !== '/token' && (
                <Button onClick={() => handleRouteChange(currentTokenId)} noBorder arrowLeft>
                  {t('loginMask.myExhibition')}
                </Button>
              )}
              <Button onClick={() => handleLogout()} noBorder arrowLeft>
                Logout
              </Button>
            </div>
          </>
        ) : (
          <form onSubmit={e => handleSubmit(e)} className="loginMask__form" noValidate>
            {topLine && (
              <Headline tag="p" subline className="loginMask__form-topline">
                {topLine}
              </Headline>
            )}
            <div className="loginMask__form-container">
              <div className="loginMask__form-group">
                <div className="loginMask__form-icon" aria-hidden="true">
                  <ArrowRight />
                </div>
                <TokenInput
                  insideNavigation
                  className="loginMask__input"
                  onFocus={() => setError(false)}
                  onChange={e => setTokenId(e.target.value)}
                  name={input.name}
                  value={tokenId}
                  placeholder={input.placeholder}
                  required={input.required}
                />
              </div>
              <div className="loginMask__submit">
                <button type="submit">
                  <span className="sr-only">{t('submitExhibitionCode')}</span>
                  <LoginIcon
                    role="img"
                    ariaLabel={t('submitExhibitionCode')}
                    className="loginMask__icon"
                  />
                </button>
              </div>
            </div>
            {error && (
              <Copy className="tokenInput__instructions" small>
                {t('loginMask.error')}
              </Copy>
            )}
            {!isLoggedIn && (
              <Copy className="loginMask__hint" small>
                {hintText}
              </Copy>
            )}
          </form>
        )}
      </div>
    </StyledCompactLoginMask>
  );
};

StickyLoginMask.propTypes = {
  action: PropTypes.string,
  headline: PropTypes.string,
  logoutHeadline: PropTypes.string,
  topLine: PropTypes.string,
  subline: PropTypes.string,
  copy: PropTypes.string,
  hintText: PropTypes.string,
  input: PropTypes.shape(),
  isLoggedIn: PropTypes.bool,
  updateLogin: PropTypes.func,
  currentTokenId: PropTypes.string,
  insideNavigation: PropTypes.bool,
  isTeaserCard: PropTypes.bool
};

export default StickyLoginMask;
